import { apiJson } from '@/lib/api';

export type ExchangeStatus = 'ON_TIME' | 'LATE' | 'NO_SHOW' | 'CANCELLED';
export type ExchangeType = 'PICKUP' | 'DROPOFF';
export type LateReason =
  | 'TRAFFIC'
  | 'EMERGENCY'
  | 'WORK'
  | 'WEATHER'
  | 'OTHER';

export type ExchangeLog = {
  id: number;
  created_at: string;
  updated_at: string;
  household_id: number;
  beneficiary_id: number;
  custody_schedule_id: number;
  creator_user_id: number;
  creator_user_name: string | null;
  scheduled_date: string;
  scheduled_time: string | null;
  exchange_type: ExchangeType;
  status: ExchangeStatus;
  minutes_late: number | null;
  note: string | null;
  late_reason: LateReason | null;
  custody_exception_id: number | null;
};

export type ExchangeLogListResponse = {
  exchange_logs: ExchangeLog[];
};

export type CreateExchangeLogData = {
  beneficiary_id: number;
  scheduled_date: string;
  scheduled_time?: string;
  exchange_type: ExchangeType;
  status: ExchangeStatus;
  minutes_late?: number;
  note?: string;
  late_reason?: LateReason;
};

export type UpdateExchangeLogData = {
  status?: ExchangeStatus;
  minutes_late?: number;
  note?: string;
  late_reason?: LateReason;
};

type GetTokenFn = (options?: { template?: string }) => Promise<string | null>;

export async function createExchangeLog(
  getToken: GetTokenFn,
  data: CreateExchangeLogData
): Promise<ExchangeLog> {
  return apiJson<ExchangeLog>(getToken, '/v1/exchange-logs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getExchangeLogs(
  getToken: GetTokenFn,
  params: {
    start_date?: string;
    end_date?: string;
    beneficiary_id?: number;
    status?: string;
  }
): Promise<ExchangeLogListResponse> {
  const searchParams = new URLSearchParams();
  if (params.start_date) searchParams.set('start_date', params.start_date);
  if (params.end_date) searchParams.set('end_date', params.end_date);
  if (params.beneficiary_id)
    searchParams.set('beneficiary_id', String(params.beneficiary_id));
  if (params.status) searchParams.set('status', params.status);

  const qs = searchParams.toString();
  return apiJson<ExchangeLogListResponse>(
    getToken,
    `/v1/exchange-logs${qs ? `?${qs}` : ''}`
  );
}

export async function updateExchangeLog(
  getToken: GetTokenFn,
  id: number,
  data: UpdateExchangeLogData
): Promise<ExchangeLog> {
  return apiJson<ExchangeLog>(getToken, `/v1/exchange-logs/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteExchangeLog(
  getToken: GetTokenFn,
  id: number
): Promise<void> {
  return apiJson<void>(getToken, `/v1/exchange-logs/${id}`, {
    method: 'DELETE',
  });
}
